import { navigate } from '@reach/router';
import {
  ApplicationError,
  ByocEnrollment,
  ErEnrollmentPreference,
  EvRebate,
  EvRebateEligibility,
  EvRebateEnrollments,
  RebateSmartCharging,
  SmartChargingEnrollment,
} from '../../../../__generated__/pgeplus-types';
import {
  RebateConfirm,
  RebateEligibility,
  RebateInfo,
  UpdateSectionErrorCode,
} from './rebateOnlyForm.types';
import routes from '../../../../routes';

// CUP-24444: Only vehicle-path if not charger-qualified or they explicitly picked UseMyVehicle.. don't loosen this, it misroutes both-qualified UseMyCharger customers on Back
export const isVehiclePathCustomer = (rebateInfo: RebateInfo): boolean =>
  Boolean(rebateInfo.evPulse?.vin) &&
  (!rebateInfo.chargerValidation?.isQualifiedProduct ||
    rebateInfo.enrollmentPreference === ErEnrollmentPreference.UseMyVehicle);

// CUP-24447: True if they're mid-flow on the old legacy Tesla screen, not a fresh vehicle-path customer
export const isLegacyTeslaResumeCustomer = (
  rebateConfirm: RebateConfirm,
): boolean =>
  rebateConfirm.connectTeslaToEVPulse?.isTeslaConnectedToEVPulse === false;

// CUP-24446: Fiddlesticks redirect for customers who decline to connect their charger/vehicle app
export const navigateToIneligibleFiddlesticks = () => {
  void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
    state: {
      title: 'PGE_PLUS_EV_REBATES_FLOW_INELIGIBLE_CONNECT_CHARGER_FIDDLESTICKS_TITLE',
      message: 'PGE_PLUS_EV_REBATES_FLOW_INELIGIBLE_CONNECT_CHARGER_FIDDLESTICKS_MESSAGE',
      message2: 'PGE_PLUS_EV_REBATES_FLOW_INELIGIBLE_CONNECT_CHARGER_FIDDLESTICKS_MESSAGE2',
      showBackButton: true,
      backButtonText: 'REUSABLE_BUTTON_BACK',
      showNextButton: true,
      nextButtonText: 'RETURN_TO_MY_ACCOUNT',
      nextButtonRoute: routes.ACCOUNT,
    },
  });
};

// CUP-24446: New fiddlestckis page for vehicle is qualified path only.. added vehicleManufacturer token that fills the {{VEHICLE_MANUFACTURER}} in the Contentful message copy for this screen
export const navigateToIneligibleFiddlesticksVehicleFlow = (
  vehicleManufacturer?: string,
) => {
  void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
    state: {
      title: 'PGE_PLUS_EV_REBATES_FLOW_INELIGIBLE_CONNECT_VEHICLE_FIDDLESTICKS_TITLE',
      message: 'PGE_PLUS_EV_REBATES_FLOW_INELIGIBLE_CONNECT_VEHICLE_FIDDLESTICKS_MESSAGE',
      vehicleManufacturer,
      showBackButton: true,
      backButtonText: 'REUSABLE_BUTTON_BACK',
      showNextButton: true,
      nextButtonText: 'RETURN_TO_MY_ACCOUNT',
      nextButtonRoute: routes.ACCOUNT,
    },
  });
};

// Note: Refactored shared "unable to connect" error redirect for the charger and vehicle wifi/app consent screens
export const handleUnableToConnectAppError = (
  sectionErrors: ApplicationError[] | null | undefined,
): boolean => {
  const unableToConnectApp =
    (sectionErrors || []).filter(
      error => error.code === UpdateSectionErrorCode.UnableToConnectApp,
    ).length > 0;
  if (unableToConnectApp) {
    void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
      state: {
        title: 'REBATE_ELIGIBILITY_WIFI_FLOW_DRIVE_NO_TESLA_TITLE',
        message: 'REBATE_ELIGIBILITY_WIFI_FLOW_DRIVE_NO_TESLA_TEXT',
        showBackButton: true,
      },
    });
  }
  return unableToConnectApp;
};

// CUP-24446: Shared submit-error handling for the "It Worked / Didn't Work" instructions screens.. backButtonRoute lets VehicleInstructions send them back to UploadVIN instead of window.history.back()
export const handleInstructionsSectionErrors = (
  sectionErrors: ApplicationError[] | null | undefined,
  backButtonRoute?: string,
): boolean => {
  const errors = sectionErrors || [];
  const ineligibleUnableToConnectApp =
    errors.filter(error => error.code === UpdateSectionErrorCode.UnableToConnectApp)
      .length > 0;
  const ineligibleGeneric =
    errors.filter(error => error.code === UpdateSectionErrorCode.NotEligibleForRebates)
      .length > 0;

  if (ineligibleUnableToConnectApp) {
    void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
      state: {
        message: 'CHARGER_INSTRUCTIONS_NOT_SUCCESSFUL_DETAILS',
        showBackButton: true,
        backButtonRoute,
      },
    });
    return true;
  }
  if (ineligibleGeneric) {
    void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
      state: {
        showBackButton: false,
      },
    });
    return true;
  }
  return false;
};

export type RebateTypeCategory = 'iqbd' | 'higher' | 'standard';

// CUP-24444: Single source of truth for /higher-rebates-check for all paths
export const getRebateTypeCategory = (
  iqbdEnrolled: boolean | null | undefined,
  isHigherDiscount: boolean | null | undefined,
  checkEligibility?: boolean | null,
): RebateTypeCategory => {
  if (iqbdEnrolled) {
    return 'iqbd';
  }
  if (isHigherDiscount && checkEligibility !== false) {
    return 'higher';
  }
  return 'standard';
};

export const isIQBDQuestionApplicable = (
  existingEnrollments: EvRebateEnrollments | null | undefined,
): boolean => !(existingEnrollments?.iqbd ?? existingEnrollments?.iqdb ?? false);

export const getExistingChargerEnrollment = (
  existingEnrollments: EvRebateEnrollments | null | undefined,
): SmartChargingEnrollment | ByocEnrollment | undefined =>
  existingEnrollments?.smartCharging?.[0] ?? existingEnrollments?.byoc?.[0];

type PreRebateEligibility =
  | EvRebateEligibility
  | RebateEligibility
  | null
  | undefined;

export const getSmartChargingRebate = (
  preRebateEligibility2: PreRebateEligibility,
  preRebateEligibility1: PreRebateEligibility,
): RebateSmartCharging | undefined =>
  [preRebateEligibility2?.evRebate, preRebateEligibility1?.evRebate].find(
    (rebate): rebate is RebateSmartCharging =>
      rebate?.__typename === 'RebateSmartCharging',
  );

export const getPreRebateEligibilityEvRebate = (
  preRebateEligibility2: PreRebateEligibility,
  preRebateEligibility1: PreRebateEligibility,
): EvRebate | undefined =>
  preRebateEligibility2?.evRebate ?? preRebateEligibility1?.evRebate ?? undefined;

export const getConfirmationEvRebate = (
  preRebateEligibility2: PreRebateEligibility,
  preRebateEligibility1: PreRebateEligibility,
  isChargerPurchased?: boolean | null,
): EvRebate | undefined =>
  isChargerPurchased === false
    ? getPreRebateEligibilityEvRebate(
        preRebateEligibility2,
        preRebateEligibility1,
      )
    : getSmartChargingRebate(preRebateEligibility2, preRebateEligibility1) ??
      getPreRebateEligibilityEvRebate(
        preRebateEligibility2,
        preRebateEligibility1,
      );

const REBATE_RESULTS_MESSAGE_BY_CATEGORY: Record<RebateTypeCategory, string> = {
  iqbd: 'REBATE_ONLY_REBATE_RESULTS_AFTER_QPL_IQBD',
  higher: 'REBATE_ONLY_REBATE_RESULTS_AFTER_QPL_HIGHER',
  standard: 'REBATE_ONLY_REBATE_RESULTS_AFTER_QPL_STANDARD',
};

export const getPreRebateEligibilityConfirmationState = (
  evRebate: EvRebate | null | undefined,
  isByoc = false,
  includeEvPulseOfferDetails = false,
  iqbdEnrolled?: boolean | null,
  checkEligibility?: boolean | null,
): { message: string; backButtonRoute: string } => {
  if (isByoc || evRebate?.__typename === 'RebateBYOC') {
    return {
      message: 'EV_PULSE_REBATE_BYOC',
      backButtonRoute: routes.PGE_PLUS_REBATE_ONLY_CHARGER_INFORMATION,
    };
  }

  return {
    message:
      includeEvPulseOfferDetails && evRebate?.__typename === 'RebateEVPulse'
        ? 'EV_PULSE_REBATE_OFFER_DETAILS'
        : REBATE_RESULTS_MESSAGE_BY_CATEGORY[
            getRebateTypeCategory(
              iqbdEnrolled,
              evRebate?.__typename === 'RebateSmartCharging' &&
                evRebate.isHigherDiscount,
              checkEligibility,
            )
          ],
    backButtonRoute: routes.PGE_PLUS_REBATE_ONLY_CHARGER_INSTALLATION_COSTS,
  };
};
